import { inject, Injectable } from '@angular/core';
import {
  ContentFeedNotification,
  NewsItem
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { NotificationAdapterService } from '../../adapters/notification/notification-adapter.service';
import { HeaderFooterMachCore } from '../../models/header-footer-mach-core';
import { NotificationFeedObject } from '../../models/notification/notification.interfaces';
import { HeaderFooterMachCoreContentService } from '../../services/header-footer-mach-core/header-footer-mach-core-content.service';
import { NewsService } from '../../services/news/news.service';
@Injectable({
  providedIn: 'root'
})
export class NotificationFacadeService {
  adapter = inject(NotificationAdapterService);
  news = inject(NewsService);
  headerFooterContent = inject(HeaderFooterMachCoreContentService);
  notificationDetails$ = new BehaviorSubject<ContentFeedNotification>(
    {} as ContentFeedNotification
  );

  feedNotificationObj: NotificationFeedObject = {
    feedCounterValue: 0,
    lastVisitedFeedAdvisories: null,
    lastVisitedFeedExpiry: null
  };

  feedCounterValue = 0;

  init(): void {
    this.headerFooterContent
      .getHeaderFooterMachCoreContent$()
      .pipe(take(1))
      .subscribe((content) => {
        this.notificationDetails$.next(this.adapter.getContent(content));
        this.setInitialLocalStorage(content);
      });
  }

  setInitialLocalStorage(content: HeaderFooterMachCore): void {
    if (!content.advisories?.AdvisoriesList?.length) return;

    let storedFeedNotificationObj = {} as NotificationFeedObject;

    try {
      const storedData = localStorage.getItem('feedNotificationObj');
      if (storedData) {
        storedFeedNotificationObj = JSON.parse(storedData);
      }
    } catch (error) {
      console.error('Error parsing stored notification data:', error);
      localStorage.removeItem('feedNotificationObj');
    }

    this.feedNotificationObj = storedFeedNotificationObj;

    this.extractNewAndCurrentDate();
    this.updateFeedCounter(content);
  }

  updateFeedCounter(content: HeaderFooterMachCore): void {
    const advisories = content.advisories.AdvisoriesList.slice(0, 3);
    const lastVisitedDate = this.feedNotificationObj.lastVisitedFeedAdvisories
      ? new Date(this.feedNotificationObj.lastVisitedFeedAdvisories)
      : null;

    this.feedCounterValue = lastVisitedDate
      ? advisories.reduce(
          (count, advisory) =>
            this.parseAdvisoryDate(advisory.timestamp) > lastVisitedDate
              ? count + 1
              : count,
          0
        )
      : advisories.length;
  }

  extractNewAndCurrentDate(): void {
    const currentDate = new Date().toISOString().split('T')[0];
    if (this.feedNotificationObj.lastVisitedFeedExpiry) {
      const feedExpiryDate = new Date(
        this.feedNotificationObj.lastVisitedFeedExpiry
      )
        .toISOString()
        .split('T')[0];
      if (currentDate === feedExpiryDate) {
        this.feedNotificationObj.lastVisitedFeedAdvisories = null;
        this.feedNotificationObj.lastVisitedFeedExpiry = null;
      }
    }
  }

  parseAdvisoryDate(feedDate: string): Date {
    const [, month, day, year] =
      feedDate.match(/(\w{3})\.?\s+(\d{1,2}),\s+(\d{4})/) || [];
    const monthIndex =
      'JanFebMarAprMayJunJulAugSepOctNovDec'.indexOf(month) / 3;
    return new Date(Number(year), monthIndex, Number(day));
  }

  getNews(): void {
    this.news
      .getNews()
      .pipe(
        take(1),
        catchError(() => {
          const errorState: ContentFeedNotification = {
            ...this.notificationDetails$.getValue(),
            news: [],
            showEmptyMessageNews: true
          };
          this.notificationDetails$.next(errorState);

          return of([]);
        })
      )
      .subscribe({
        next: (news) => {
          const notificationDetailCopy: ContentFeedNotification = {
            ...this.notificationDetails$.getValue()
          };

          const transform: NewsItem[] = news.map((n) => ({
            title: n.title,
            link: n.link,
            publishedTime: n.publishedtime
          }));

          notificationDetailCopy.news = transform;
          notificationDetailCopy.showEmptyMessageNews = transform.length === 0;
          this.notificationDetails$.next(notificationDetailCopy);
        }
      });
  }
}
